import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
// import { Link } from 'react-router-dom';  // SIGNED-ICF-BINDER
import { AlertTriangle, ExternalLink, FileSignature, /* FolderOpen, SIGNED-ICF-BINDER */ Info, Loader2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { consent, type ConsentRecordRow } from '@/api/client';
import { fmtDate, fmtDateTime } from '@/lib/dates.js';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';

/**
 * Subject page - Signed Consents section.
 *
 * Client feedback #5: coordinators need to see the full signed-ICF
 * history on a subject's detail page (not just the current active
 * version at study level). Each row shows version, signer role,
 * signed / withdrawn / declined state, and links out to the signed
 * PDF plus the eISF binder mirror (Participant Materials -> Participant
 * Consent -> Signed ICF).
 *
 * The mirror itself is already wired end-to-end in the BE
 * (confirmSignedUpload + signedConsentArchive job both call
 * mirrorToEisfBinder with artifactType='Signed ICF'). This section is
 * the reader.
 *
 * SIGNED-ICF-BINDER (16 Oct 2026): the binder filing is paused (PHI), so the
 * "Open in Binder" button below is commented out until the client decides.
 */

function stateLabel(rec: ConsentRecordRow): { label: string; className: string } {
  if (rec.withdrawn_at) return { label: 'Withdrawn', className: 'bg-surface-muted text-ink-secondary' };
  if (rec.declined_at) return { label: 'Declined', className: 'bg-danger-100 text-danger-700' };
  if (rec.signed_at) return { label: 'Signed', className: 'bg-emerald-100 text-emerald-800' };
  return { label: 'In progress', className: 'bg-amber-100 text-amber-800' };
}

/**
 * Bug found live (UAT, 2026-08): "Signed ICF is metadata is correct, but
 * the system loading unsigned template PDF in place of signed PDF." The
 * old link here pointed straight at `docuseal_signed_pdf_url` — a raw,
 * unauthenticated DocuSeal attachment URL that can still serve the
 * pre-flatten template. Now presigns from the archived, known-correct
 * copy via the backend instead. Opens a blank tab SYNCHRONOUSLY on click
 * (before the async presign call) so the browser doesn't treat it as a
 * popup — a tab opened after an await is blocked by most browsers.
 */
function ViewSignedPdfButton({ consentRecordId }: { consentRecordId: string }) {
  const [loading, setLoading] = useState(false);

  async function handleClick() {
    const win = window.open('', '_blank');
    setLoading(true);
    try {
      const { url } = await consent.getSignedDocumentUrl(consentRecordId);
      if (win) win.location.href = url;
      else window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      win?.close();
      toast.error(errorText(err, 'Failed to load the signed document.'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      className="inline-flex items-center gap-1 rounded border border-surface-border bg-white px-2 py-1 text-[11px] font-medium text-ink-primary hover:bg-surface-muted/50 disabled:cursor-not-allowed disabled:opacity-60"
    >
      {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <ExternalLink className="h-3 w-3" />}
      View signed PDF
    </button>
  );
}

/**
 * 2026-09-04 — self-heal for a signed record still missing its archived
 * PDF. Archival now runs inline right after signing (backend), but if
 * that attempt hit a transient failure, silently retry once here by
 * calling the same "view signed PDF" endpoint in the background (it
 * retries the archive itself if still missing) — no user click needed.
 * On success, invalidate so the section re-renders with the real button.
 */
function ArchivingPill({ consentRecordId, onArchived }: { consentRecordId: string; onArchived: () => void }) {
  useEffect(() => {
    let cancelled = false;
    consent
      .getSignedDocumentUrl(consentRecordId)
      .then(() => {
        if (!cancelled) onArchived();
      })
      .catch(() => {
        // Still not ready — leave the pill showing; a future page visit
        // (or a manual refresh) will try again.
      });
    return () => {
      cancelled = true;
    };
  }, [consentRecordId, onArchived]);

  return (
    <span
      className="inline-flex items-center gap-1 rounded border border-surface-border bg-surface-muted/40 px-2 py-1 text-[11px] font-medium text-ink-muted"
      title="The signed PDF is archived shortly after signing — check back in a moment."
    >
      <Loader2 className="h-3 w-3 animate-spin" />
      Archiving signed PDF…
    </span>
  );
}

function signerRoleLabel(rec: ConsentRecordRow): string {
  switch (rec.signer_role) {
    case 'subject': return 'Subject';
    case 'parent': return 'Parent';
    case 'assenting_minor': return 'Assenting minor';
    case 'lar': return 'Legally authorised representative';
    default: return rec.signer_role ?? 'Subject';
  }
}

export function SubjectSignedConsentsSection({ subjectId }: { subjectId: string }) {
  const qc = useQueryClient();
  const queryKey = ['subject', subjectId, 'consent-records'];
  const q = useQuery({
    queryKey,
    queryFn: () => consent.listRecordsForSubject(subjectId),
    enabled: !!subjectId,
  });

  const records = q.data?.records ?? [];

  return (
    <section className="card p-4">
      <div className="mb-3 flex items-center gap-2">
        <FileSignature className="h-4 w-4 text-accent-600" />
        <h2 className="text-sm font-semibold text-ink-primary">Signed Consents</h2>
        <span className="ml-auto text-xs text-ink-muted">
          {records.length === 0 ? 'None on file' : `${records.length} record${records.length === 1 ? '' : 's'}`}
        </span>
      </div>

      {q.isLoading && (
        <div className="flex items-center gap-2 text-sm text-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      {q.isError && (
        <div className={`flex items-start gap-2 rounded p-3 text-sm ${errorToneClass(q.error)}`}>
          {isPermissionError(q.error) ? <Info className="mt-0.5 h-4 w-4 shrink-0"/> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
          <span>{errorText(q.error, 'Failed to load consent history.')}</span>
        </div>
      )}

      {!q.isLoading && !q.isError && records.length === 0 && (
        <p className="text-sm text-ink-muted">
          No consent has been signed yet for this subject. Start the consent flow from the
          Onboarding wizard or Remote Consent trigger above.
        </p>
      )}

      {records.length > 0 && (
        <ul className="divide-y divide-surface-border">
          {records.map((rec) => {
            const state = stateLabel(rec);
            return (
              <li key={rec.consent_record_id} className="py-2.5 text-xs">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-ink-primary">
                    {rec.record_type === 're_consent' ? 'Re-consent' : 'Initial consent'}
                    {rec.new_version_label ? ` → ${rec.new_version_label}` : ''}
                  </span>
                  <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold ${state.className}`}>
                    {rec.withdrawn_at && <XCircle className="h-2.5 w-2.5" />}
                    {state.label}
                  </span>
                  <span className="text-[10px] uppercase tracking-wide text-ink-muted">
                    {signerRoleLabel(rec)}
                  </span>
                </div>
                <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-ink-muted">
                  {rec.signed_at && <span>Signed {fmtDateTime(rec.signed_at)}</span>}
                  {rec.declined_at && <span>Declined {fmtDate(rec.declined_at)}</span>}
                  {rec.withdrawn_at && (
                    <span>
                      Withdrawn {fmtDate(rec.withdrawn_at)}
                      {rec.withdrawal_reason ? ` · ${rec.withdrawal_reason}` : ''}
                    </span>
                  )}
                  {rec.old_version_label && rec.new_version_label && (
                    <span>{rec.old_version_label} → {rec.new_version_label}</span>
                  )}
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  {rec.signed_pdf_s3_key ? (
                    <ViewSignedPdfButton consentRecordId={rec.consent_record_id} />
                  ) : (
                    rec.signed_at && (
                      <ArchivingPill
                        consentRecordId={rec.consent_record_id}
                        onArchived={() => qc.invalidateQueries({ queryKey })}
                      />
                    )
                  )}
                  {/*
                     SIGNED-ICF-BINDER: PAUSED (client, 16 Oct 2026). A signed ICF carries the subject's
                     PHI, so for now it is NOT filed to the eISF binder: it stays in the consent
                     archive and is opened from the subject page (View signed PDF). The client
                     has not decided yet, so the working code is kept here, commented out. To
                     turn it back on, uncomment every block tagged SIGNED-ICF-BINDER (search the repo).
                  <Link
                    to="/binder"
                    className="inline-flex items-center gap-1 rounded border border-surface-border bg-surface-muted/40 px-2 py-1 text-[11px] font-medium text-ink-secondary hover:bg-surface-muted"
                    title="Open the eISF binder — signed ICFs are mirrored to Participant Materials → Participant Consent → Signed ICF"
                  >
                    <FolderOpen className="h-3 w-3" />
                    Open in Binder
                  </Link>
                  */}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
